var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
export const getProp = (obj, path, defaultValue) => {
    const prop = path.split('.').reduce(function (previous, current) {
        return previous ? previous[current] : undefined;
    }, obj || {});
    if (prop) {
        return prop;
    }
    return defaultValue;
};
const getFetchOptions = ({ csrf, baseUri }) => {
    const headers = {
        'Content-Type': 'application/json',
    };
    if (csrf) {
        headers['x-csrf-token'] = csrf;
    }
    return {
        // Default options are marked with *
        method: 'POST',
        mode: baseUri ? 'cors' : 'same-origin',
        cache: 'no-cache',
        credentials: 'same-origin',
        headers,
        redirect: 'follow',
        referrerPolicy: 'no-referrer', // no-referrer, *no-referrer-when-downgrade, origin, origin-when-cross-origin, same-origin, strict-origin, strict-origin-when-cross-origin, unsafe-url
    };
};
const getBaseUrl = ({ baseUri, path = '/idapps/graphql' }) => {
    const win = typeof window === 'undefined' ? {} : window;
    const loc = win.location || {};
    return new URL(path, baseUri || 'https://' + (loc.host || 'localhost.paypal.com')).href;
};
const getFetchFn = (customFetch) => {
    let fetchFn = fetch;
    if (typeof customFetch === 'function') {
        console.log('using customFetch');
        fetchFn = customFetch;
    }
    return fetchFn;
};
export const postGql = ({ baseUri, csrf, data = {}, sessionID, timeout, env, customFetch }) => __awaiter(void 0, void 0, void 0, function* () {
    var _a, _b, _c, _d;
    const fetchUrl = getBaseUrl({ baseUri });
    if (sessionID) {
        data._sessionID = sessionID;
    }
    if ((_b = (_a = data === null || data === void 0 ? void 0 : data.variables) === null || _a === void 0 ? void 0 : _a.clientInfo) === null || _b === void 0 ? void 0 : _b.rData) {
        const { fn_sync_data } = JSON.parse(decodeURIComponent((_d = (_c = data === null || data === void 0 ? void 0 : data.variables) === null || _c === void 0 ? void 0 : _c.clientInfo) === null || _d === void 0 ? void 0 : _d.rData)) || '{}';
        data['fn_sync_data'] = fn_sync_data;
    }
    // Provision a custom abort signal for enabling timeouts in prod
    // `fetch` does not have a API to timeout a request
    // it does allow a AbortController function to write your own timeout
    // https://developer.mozilla.org/en-US/docs/Web/API/AbortController
    let signal = null;
    let signalTimeoutId;
    if (env === 'prod' && typeof timeout === 'number') {
        const controller = new AbortController();
        signalTimeoutId = setTimeout(() => controller.abort(), timeout);
        signal = controller.signal;
    }
    try {
        const fetchFn = getFetchFn(customFetch);
        const response = yield fetchFn(fetchUrl, Object.assign(Object.assign({}, getFetchOptions({ baseUri, csrf })), { signal, body: JSON.stringify(data) }));
        if (response.status === 403) {
            throw 'invalid_ctxId';
        }
        if (response.status !== 200) {
            throw `Invalid request ${fetchUrl}`;
        }
        if (signalTimeoutId) {
            clearTimeout(signalTimeoutId);
        }
        return yield response.json(); // parses JSON response into native JavaScript objects
    }
    catch (err) {
        return err;
    }
});
export const getLogger = () => {
    const memo = {
        events: [],
        flowContext: {},
    };
    const extract = () => {
        const events = [...memo.events];
        memo.events = [];
        return { events, flowContext: memo.flowContext };
    };
    return {
        track: (evt) => memo.events.push(evt),
        extract,
        addToFlowContext: (additionalContext) => {
            memo.flowContext = Object.assign(Object.assign({}, memo.flowContext), additionalContext);
        },
        deleteFromFlowContext: (fieldName) => {
            delete memo.flowContext[fieldName];
        },
        setFlowContext: (flowContext = {}) => {
            memo.flowContext = flowContext;
        },
        flush: ({ baseUri, csrf, csrfNonce, customFetch, sessionID }) => {
            if (memo.events.length === 0 || (!csrf && !csrfNonce)) {
                return;
            }
            const fetchUrl = getBaseUrl({ baseUri, path: '/idapps/log' });
            const body = {
                log: extract(),
            };
            // Add session ID only for the cookie disabled scenario
            // (for browsers with cookies enabled, sessionID will not exist)
            if (sessionID) {
                body._sessionID = sessionID;
            }
            // As soon as IWC is loaded, a signal is dispatched
            // to track this event, this will have a csrfNonce
            // as there is no csrf at this point in time
            if (csrfNonce) {
                body.csrfNonce = csrfNonce;
            }
            const fetchFn = getFetchFn(customFetch);
            // fire and forget
            fetchFn(fetchUrl, Object.assign(Object.assign({}, getFetchOptions({ baseUri, csrf })), { body: JSON.stringify(body) }));
        },
    };
};
export const getUserAgent = () => typeof navigator === 'object' && navigator.userAgent;
export const isMobile = () => /Mobi/.test(getUserAgent());
export const isAndroid = () => /android/i.test(getUserAgent());
export const isSafari = () => {
    let userAgent = getUserAgent();
    return /^((?!chrome|android).)*safari/i.test(userAgent);
};
export const isSafariKeychainPrefilledField = (field) => {
    if (!field || !field.value || !field.value.length) {
        return false;
    }
    return field.value.length > 0 || window.getComputedStyle(field).backgroundColor === 'rgb(250, 255, 189)';
};
export const str2buff = (str) => {
    return Uint8Array.from(window.atob(str), (c) => {
        return c.charCodeAt(0);
    });
};
export const buff2str = (strBuff) => {
    return window.btoa(new Uint8Array(strBuff).reduce((s, byte) => {
        return s + String.fromCharCode(byte);
    }, ''));
};
export const getCoBrand = (countryCode = 'US') => (countryCode === 'GB' ? 'uk' : countryCode.toLowerCase());
export const b64DecodeUnicode = (str) => {
    // Going backwards: from bytestream, to percent-encoding, to original string.
    // https://stackoverflow.com/questions/30106476/using-javascripts-atob-to-decode-base64-doesnt-properly-decode-utf-8-strings/52647441#52647441
    return decodeURIComponent(atob(str)
        .split('')
        .map(function (c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    })
        .join(''));
};
